import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "../api/axios";
import { hasAnyRole } from "../utils/auth";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  FORM_SCHEMA,
  I3_PARAMETERS,
  I4_LEVELS,
  I4_PARAMETERS,
  SUMMARY_ROWS,
} from "./appraisalFormSchema";

const EVALUATOR_ROLES = ["auditor", "hod", "hr", "admin", "super_admin"];
const HOD_LEVEL_ROLES = ["hod", "hr", "admin", "super_admin"];

// Academic year runs June–May. June 2026 -> "2026-27", June 2027 -> "2027-28".
const getCurrentAcademicStartYear = () => {
  const now = new Date();
  return now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1;
};

const buildAcademicYearOptions = (count = 10) => {
  const latestStartYear = getCurrentAcademicStartYear();
  return Array.from({ length: count }, (_, index) => {
    const startYear = latestStartYear - index;
    const endYear = String(startYear + 1).slice(-2);
    return `${startYear}-${endYear}`;
  });
};
const ACADEMIC_YEAR_OPTIONS = buildAcademicYearOptions();
const SECTION_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I"];

const SECTION_TITLES = {
  A: "A. Educational Qualification Up-gradation (Ph.D.* - 10, Other - 02  Max.: 10)",
  B: "B. Outcome-Based Teaching & Learning (Max.: 50)",
  C: "C. Research (Max.: 60)",
  D: "D. Development Activities & IPR (Max.: 15)",
  E: "E. Consultancy & Testing (Max.: 15)",
  F: "F. FDPs / Training / Skilling / E-Resource (Max.: 35)",
  G: "G. Awards and Recognition / Membership (Max.: 10)",
  H: "H. Industry Connect (Max.: 10)",
  I: "I. Other Activities (Max.: 45)",
};

const fmt = (v) => (v === null || v === undefined || v === "" ? "" : v);

/* ---- styling helpers (document look) ---- */
const cellSx = {
  border: "1px solid #9ca3af",
  fontSize: "0.8rem",
  p: "6px 8px",
  verticalAlign: "top",
};
const headCellSx = {
  ...cellSx,
  fontWeight: 700,
  backgroundColor: "#dbe5f1",
  color: "#1f2937",
  textAlign: "center",
};

function Appraisal() {
  const isModerator = localStorage.getItem("is_moderator") === "true";
  const isEvaluator = hasAnyRole(...EVALUATOR_ROLES) || isModerator;
  const isHodLevel = hasAnyRole(...HOD_LEVEL_ROLES) || isModerator;
  // A "pure HOD" (can set portfolio but not enter evaluator marks) has the hod
  // role but no auditor/global evaluator role.
  const isPureHod =
    hasAnyRole("hod") && !hasAnyRole("auditor", "hr", "admin", "super_admin") && !isModerator;

  const [tab, setTab] = useState(0);
  const [academicYear, setAcademicYear] = useState(ACADEMIC_YEAR_OPTIONS[0]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Global appraisal visibility (super_admin toggle). null = still loading.
  const [appraisalEnabled, setAppraisalEnabled] = useState(null);

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("token");
    axios
      .get("/appraisal/settings", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (active) setAppraisalEnabled(res.data?.appraisal_enabled !== false);
      })
      .catch(() => {
        if (active) setAppraisalEnabled(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const [data, setData] = useState(null);
  const [scores, setScores] = useState({});
  const [evaluating, setEvaluating] = useState(false);
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // Draft values for the "Add row" forms, keyed by criterion -> { colHeader: value }
  const [entryDrafts, setEntryDrafts] = useState({});

  const flash = (setter, msg) => {
    setter(msg);
    setTimeout(() => setter(""), 4000);
  };

  const hydrateScores = (criteria) => {
    const map = {};
    (criteria || []).forEach((c) => {
      map[c.key] = {
        self_score: c.self_score ?? "",
        evaluator_score: c.evaluator_score ?? "",
        hod_score: c.hod_score ?? "",
        remarks: c.remarks ?? "",
      };
    });
    setScores(map);
  };

  const loadMine = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `/appraisal/me?academic_year=${encodeURIComponent(academicYear)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
      hydrateScores(res.data.criteria);
      setEvaluating(false);
      setSelectedId(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load appraisal");
    } finally {
      setLoading(false);
    }
  }, [academicYear]);

  const loadList = useCallback(async () => {
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `/appraisal/list?academic_year=${encodeURIComponent(academicYear)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setList(res.data.appraisals || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load appraisals");
    }
  }, [academicYear]);

  const loadOne = useCallback(async (appraisalId) => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/appraisal/${appraisalId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      hydrateScores(res.data.criteria);
      setEvaluating(true);
      setSelectedId(appraisalId);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load appraisal");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (appraisalEnabled === false) return; // feature disabled — don't fetch
    if (tab === 0) {
      loadMine();
    } else {
      loadList();
      setData(null);
    }
  }, [tab, loadMine, loadList, appraisalEnabled]);

  const updateScore = (key, field, value) => {
    setScores((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  /* Live totals per section + overall from on-screen inputs. */
  const totals = useMemo(() => {
    const sectionSelf = {};
    const sectionEval = {};
    SECTION_LETTERS.forEach((l) => {
      sectionSelf[l] = 0;
      sectionEval[l] = 0;
    });
    let finalTotal = 0;
    (data?.criteria || []).forEach((c) => {
      const s = scores[c.key] || {};
      if (c.info) return;
      if (c.scoredBy === "hod") {
        const h = Number(s.hod_score) || 0;
        sectionSelf[c.section] += h;
        sectionEval[c.section] += h;
        finalTotal += h;
      } else {
        const selfV = Number(s.self_score) || 0;
        const evalRaw = s.evaluator_score;
        const evalV =
          evalRaw === "" || evalRaw === null || evalRaw === undefined
            ? null
            : Number(evalRaw) || 0;
        sectionSelf[c.section] += selfV;
        sectionEval[c.section] += evalV === null ? 0 : evalV;
        finalTotal += evalV === null ? selfV : evalV;
      }
    });
    const totalMax = data?.total_max || 250;
    return {
      sectionSelf,
      sectionEval,
      finalTotal,
      final100: totalMax > 0 ? (finalTotal * 100) / totalMax : 0,
    };
  }, [data, scores]);

  const status = data?.appraisal?.status;
  const locked = status === "evaluated" || status === "finalized";

  const saveSelf = async () => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const payload = (data.criteria || [])
        .filter((c) => c.scoredBy !== "hod" && !c.info)
        .map((c) => ({
          criterion_key: c.key,
          self_score:
            scores[c.key]?.self_score === "" ? null : Number(scores[c.key]?.self_score),
          remarks: scores[c.key]?.remarks || "",
        }));
      await axios.put(
        "/appraisal/me/self-scores",
        { academic_year: academicYear, scores: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      flash(setSuccess, "Self-assessment saved");
      loadMine();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const submitMine = async () => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/appraisal/me/submit",
        { academic_year: academicYear },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      flash(setSuccess, "Appraisal submitted for evaluation");
      loadMine();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const addEntry = async (criterionKey, entryData) => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/appraisal/me/entries",
        { academic_year: academicYear, criterion_key: criterionKey, data: entryData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      flash(setSuccess, "Row added");
      await loadMine();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add row");
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (entryId) => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/appraisal/me/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      flash(setSuccess, "Row removed");
      await loadMine();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove row");
    } finally {
      setSaving(false);
    }
  };

  const saveEvaluator = async () => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const payload = (data.criteria || [])
        .filter((c) => !c.info)
        .map((c) => ({
          criterion_key: c.key,
          evaluator_score:
            scores[c.key]?.evaluator_score === ""
              ? null
              : Number(scores[c.key]?.evaluator_score),
          hod_score:
            scores[c.key]?.hod_score === "" ? null : Number(scores[c.key]?.hod_score),
        }));
      await axios.put(
        `/appraisal/${selectedId}/evaluator-scores`,
        { scores: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      flash(setSuccess, "Scores saved");
      loadOne(selectedId);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save scores");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/appraisal/${selectedId}/finalize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      flash(setSuccess, "Appraisal finalized");
      loadOne(selectedId);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to finalize");
    } finally {
      setSaving(false);
    }
  };

  /* --------- Score input widgets (in the "Self / Evaluator Score:" line) ---------
     NOTE: These are plain render functions (called like renderScoreLine(c)),
     NOT React components rendered as <ScoreLine/>. Rendering locally-defined
     components as JSX remounts them on every parent render, which drops input
     focus after each keystroke. Calling them as functions keeps the elements
     in the parent tree so React reconciles inputs in place. */
  const renderScoreLine = (c) => {
    const s = scores[c.key] || {};
    const isHodRow = c.scoredBy === "hod";
    const selfEditable = !evaluating && !locked && !isHodRow && !c.info;
    const evalEditable = evaluating && !isHodRow && !c.info && !isPureHod;
    if (c.info) return null;
    return (
      <Stack direction="row" spacing={3} sx={{ my: 0.5 }} flexWrap="wrap" alignItems="center">
        {!isHodRow && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
              Self-Assessment Score:
            </Typography>
            <TextField
              type="number"
              size="small"
              sx={{ width: 80 }}
              value={s.self_score ?? ""}
              disabled={!selfEditable}
              inputProps={{ min: 0, max: c.max || undefined, step: "0.5", style: { padding: "4px 6px" } }}
              onChange={(e) => updateScore(c.key, "self_score", e.target.value)}
            />
          </Stack>
        )}
        {!isHodRow && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
              Evaluator Score:
            </Typography>
            <TextField
              type="number"
              size="small"
              sx={{ width: 80 }}
              value={s.evaluator_score ?? ""}
              disabled={!evalEditable}
              inputProps={{ min: 0, max: c.max || undefined, step: "0.5", style: { padding: "4px 6px" } }}
              onChange={(e) => updateScore(c.key, "evaluator_score", e.target.value)}
            />
          </Stack>
        )}
      </Stack>
    );
  };

  const renderNoteLines = (lines) =>
    lines && lines.length ? (
      <Box sx={{ mb: 0.5 }}>
        {lines.map((l, i) => (
          <Typography key={i} sx={{ fontSize: "0.72rem", color: "#1d4ed8" }}>
            • {l}
          </Typography>
        ))}
      </Box>
    ) : null;

  const setDraftField = (draftKey, header, value) => {
    setEntryDrafts((prev) => ({
      ...prev,
      [draftKey]: { ...(prev[draftKey] || {}), [header]: value },
    }));
  };

  /*
   * Renders a criterion's table: verified evidence rows first, then
   * faculty-added manual rows (with a delete button), and — when the
   * faculty is editing their own unlocked appraisal — an "Add row" line.
   * `criterionKey` + `groupIndex` scope the add-row draft. Manual rows are
   * only added to the first group of a grouped criterion (or the single table).
   */
  const renderEvidenceTable = ({ columns, rows, manualRows, criterionKey, manualEntries, allowAdd, groupIndex }) => {
    const evidenceRows = rows && rows.length ? rows : [];
    const entries = manualEntries || [];
    const canAddHere = allowAdd && (groupIndex === undefined || groupIndex === 0);
    const draftKey = `${criterionKey}${groupIndex !== undefined ? `_${groupIndex}` : ""}`;
    const draft = entryDrafts[draftKey] || {};
    // Data columns (exclude the SN auto-column for the add form)
    const inputCols = columns.filter((col) => col.header !== "SN");
    const emptyPad = Math.max(0, (manualRows || 1) - evidenceRows.length - entries.length);

    const handleAdd = async () => {
      const payload = {};
      inputCols.forEach((col) => {
        if (draft[col.header]) payload[col.header] = draft[col.header];
      });
      if (Object.keys(payload).length === 0) {
        setError("Enter at least one value before adding a row.");
        return;
      }
      await addEntry(criterionKey, payload);
      setEntryDrafts((prev) => ({ ...prev, [draftKey]: {} }));
    };

    let sn = 0;
    return (
      <TableContainer sx={{ mb: 1 }}>
        <Table size="small" sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow>
              {columns.map((col, i) => (
                <TableCell key={i} sx={{ ...headCellSx, width: col.width }}>
                  {col.header}
                </TableCell>
              ))}
              {canAddHere && <TableCell sx={{ ...headCellSx, width: 48 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Verified evidence rows */}
            {evidenceRows.map((row, rIdx) => {
              sn += 1;
              return (
                <TableRow key={`ev-${rIdx}`}>
                  {columns.map((col, cIdx) => (
                    <TableCell key={cIdx} sx={cellSx}>
                      {col.header === "SN" ? sn : fmt(col.get(row, rIdx))}
                    </TableCell>
                  ))}
                  {canAddHere && (
                    <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                      <Chip label="verified" size="small" color="success" variant="outlined" />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {/* Faculty-added manual rows */}
            {entries.map((entry) => {
              sn += 1;
              return (
                <TableRow key={`me-${entry.__entry_id}`} sx={{ backgroundColor: "#fffbeb" }}>
                  {columns.map((col, cIdx) => (
                    <TableCell key={cIdx} sx={cellSx}>
                      {col.header === "SN" ? sn : fmt(entry[col.header])}
                    </TableCell>
                  ))}
                  {canAddHere && (
                    <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                      {!evaluating && !locked && (
                        <Tooltip title="Remove row">
                          <IconButton size="small" onClick={() => deleteEntry(entry.__entry_id)}>
                            <DeleteOutlineIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {/* Empty placeholder rows to preserve the printed-form look */}
            {Array.from({ length: emptyPad }).map((_, i) => (
              <TableRow key={`pad-${i}`}>
                {columns.map((col, cIdx) => (
                  <TableCell key={cIdx} sx={cellSx}>
                    {col.header === "SN" ? sn + i + 1 : ""}
                  </TableCell>
                ))}
                {canAddHere && <TableCell sx={cellSx} />}
              </TableRow>
            ))}

            {/* Add-row input line */}
            {canAddHere && (
              <TableRow sx={{ backgroundColor: "#eef2ff" }}>
                {columns.map((col, cIdx) => (
                  <TableCell key={cIdx} sx={cellSx}>
                    {col.header === "SN" ? (
                      "+"
                    ) : (
                      <TextField
                        variant="standard"
                        size="small"
                        fullWidth
                        placeholder={col.header}
                        value={draft[col.header] || ""}
                        onChange={(e) => setDraftField(draftKey, col.header, e.target.value)}
                        InputProps={{ style: { fontSize: "0.78rem" } }}
                      />
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                  <Tooltip title="Add this row">
                    <span>
                      <IconButton size="small" onClick={handleAdd} disabled={saving}>
                        <AddIcon fontSize="small" color="primary" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  /* Render one sub-criterion block (heading, notes, score line, evidence table). */
  const renderCriterionBlock = (c) => {
    const schema = FORM_SCHEMA[c.key] || {};
    const heading = `${c.key}. ${c.label}${c.max ? ` (Max.: ${c.max.toString().padStart(2, "0")})` : ""}`;
    return (
      <Box key={c.key} sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mt: 1 }}>{heading}</Typography>
        {schema.subLabel && (
          <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", fontStyle: "italic" }}>
            {schema.subLabel}
          </Typography>
        )}
        {renderNoteLines(schema.note)}
        {renderScoreLine(c)}

        {/* Grouped tables (C1) or single table.
            Faculty may add rows on their own unlocked appraisal. */}
        {(() => {
          const allowAdd = !evaluating && !locked;
          if (schema.groups) {
            return schema.groups.map((grp, gi) => {
              const rows = (c.evidence || []).filter(grp.filter);
              return (
                <Box key={gi}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mt: 0.5 }}>
                    {grp.title}
                  </Typography>
                  {renderEvidenceTable({
                    columns: grp.columns,
                    rows,
                    manualRows: 1,
                    criterionKey: c.key,
                    manualEntries: gi === 0 ? c.manual_entries : [],
                    allowAdd,
                    groupIndex: gi,
                  })}
                </Box>
              );
            });
          }
          if (schema.columns) {
            return renderEvidenceTable({
              columns: schema.columns,
              rows: c.evidence,
              manualRows: schema.manualRows,
              criterionKey: c.key,
              manualEntries: c.manual_entries,
              allowAdd,
            });
          }
          return null;
        })()}
        {schema.academicCoordinator
          ? (
            <TableContainer sx={{ mb: 1 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={headCellSx}>Self-Assessment Score</TableCell>
                    <TableCell sx={headCellSx}>Evaluator Score</TableCell>
                    <TableCell sx={headCellSx}>Name & Sign of Academic Coordinator</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={cellSx}>{scores[c.key]?.self_score ?? ""}</TableCell>
                    <TableCell sx={cellSx}>{scores[c.key]?.evaluator_score ?? ""}</TableCell>
                    <TableCell sx={cellSx}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )
          : null}
      </Box>
    );
  };

  /* I.3 Department portfolio (HoD score inputs). */
  const renderI3 = (c) => {
    const s = scores[c.key] || {};
    const canEditHod = evaluating && isHodLevel;
    return (
      <Box key={c.key} sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mt: 1 }}>
          I.3 Department Level Portfolio (Max.: 15)
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700 }}>Score given by HoD:</Typography>
        <TableContainer sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 48 }}>SN</TableCell>
                <TableCell sx={headCellSx}>Parameters</TableCell>
                <TableCell sx={{ ...headCellSx, width: 120 }}>Marks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {I3_PARAMETERS.map((p, i) => (
                <TableRow key={i}>
                  <TableCell sx={cellSx}>{i + 1}</TableCell>
                  <TableCell sx={cellSx}>{p}</TableCell>
                  <TableCell sx={cellSx}>{i === 0 ? "" : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
            I.3 Total (HoD, Max 15):
          </Typography>
          <TextField
            type="number"
            size="small"
            sx={{ width: 90 }}
            value={s.hod_score ?? ""}
            disabled={!canEditHod}
            inputProps={{ min: 0, max: c.max, step: "0.5", style: { padding: "4px 6px" } }}
            onChange={(e) => updateScore(c.key, "hod_score", e.target.value)}
          />
        </Stack>
      </Box>
    );
  };

  /* I.4 Institute portfolio (HoD score input). */
  const renderI4 = (c) => {
    const s = scores[c.key] || {};
    const canEditHod = evaluating && isHodLevel;
    return (
      <Box key={c.key} sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mt: 1 }}>
          I.4 Institute Level Portfolio (Max.: 15)
        </Typography>
        <TableContainer sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 70 }}>Level</TableCell>
                <TableCell sx={headCellSx}>Portfolios</TableCell>
                <TableCell sx={{ ...headCellSx, width: 90 }}>Max Marks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {I4_LEVELS.map((lv, i) => (
                <TableRow key={i}>
                  <TableCell sx={cellSx}>{lv.level}</TableCell>
                  <TableCell sx={cellSx}>{lv.portfolios}</TableCell>
                  <TableCell sx={cellSx}>{lv.max}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TableContainer sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 36 }}>#</TableCell>
                <TableCell sx={headCellSx}>Parameter</TableCell>
                <TableCell sx={headCellSx}>Level 1 (max 10)</TableCell>
                <TableCell sx={headCellSx}>Level 2 (max 7)</TableCell>
                <TableCell sx={headCellSx}>Level 3 (max 5)</TableCell>
                <TableCell sx={headCellSx}>Marks</TableCell>
                <TableCell sx={headCellSx}>Signature of Reporting Authority</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {I4_PARAMETERS.map((p, i) => (
                <TableRow key={i}>
                  <TableCell sx={cellSx}>{i + 1}</TableCell>
                  <TableCell sx={cellSx}>{p.name}</TableCell>
                  <TableCell sx={cellSx}>{p.l1}</TableCell>
                  <TableCell sx={cellSx}>{p.l2}</TableCell>
                  <TableCell sx={cellSx}>{p.l3}</TableCell>
                  <TableCell sx={cellSx}></TableCell>
                  <TableCell sx={cellSx}></TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={cellSx}></TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 700, textAlign: "right" }}>Total</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>15</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>10</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>05</TableCell>
                <TableCell sx={cellSx}></TableCell>
                <TableCell sx={cellSx}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
            I.4 Total (HoD, Max 15):
          </Typography>
          <TextField
            type="number"
            size="small"
            sx={{ width: 90 }}
            value={s.hod_score ?? ""}
            disabled={!canEditHod}
            inputProps={{ min: 0, max: c.max, step: "0.5", style: { padding: "4px 6px" } }}
            onChange={(e) => updateScore(c.key, "hod_score", e.target.value)}
          />
        </Stack>
      </Box>
    );
  };

  const renderSection = (letter) => {
    const items = (data?.criteria || []).filter((c) => c.section === letter);
    if (items.length === 0) return null;
    return (
      <Box key={letter} sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1rem",
            borderBottom: "2px solid #1e3a8a",
            pb: 0.5,
            mb: 1,
            color: "#1e3a8a",
          }}
        >
          {SECTION_TITLES[letter]}
        </Typography>
        {items.map((c) => {
          if (c.key === "I3") return renderI3(c);
          if (c.key === "I4") return renderI4(c);
          return renderCriterionBlock(c);
        })}
      </Box>
    );
  };

  const renderPersonalDetails = () => {
    const gi = data?.general_information || {};
    const rows = [
      ["01", "Name of the Faculty Member", gi.name],
      ["02", "Department", gi.department],
      ["03", "Educational Qualification (Highest)", gi.highest_qualification],
      ["04", "Current Designation", gi.current_designation],
      ["05", "Date of Joining", gi.date_of_joining],
    ];
    return (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#1e3a8a", borderBottom: "2px solid #1e3a8a", pb: 0.5 }}>
          General Information
        </Typography>
        <Typography sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>Personal Details</Typography>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {rows.map(([sn, label, val]) => (
                <TableRow key={sn}>
                  <TableCell sx={{ ...cellSx, width: 40 }}>{sn}</TableCell>
                  <TableCell sx={{ ...cellSx, width: 280 }}>{label}</TableCell>
                  <TableCell sx={{ ...cellSx, width: 20, textAlign: "center" }}>:</TableCell>
                  <TableCell sx={cellSx}>{fmt(val)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ ...cellSx, width: 40 }}>06</TableCell>
                <TableCell sx={{ ...cellSx, width: 280 }}>Total Experience (Yrs.)</TableCell>
                <TableCell sx={{ ...cellSx, width: 20, textAlign: "center" }}>:</TableCell>
                <TableCell sx={cellSx}>
                  Teaching: {fmt(gi.experience?.teaching)}&nbsp;&nbsp;&nbsp; Industry:{" "}
                  {fmt(gi.experience?.industry)}&nbsp;&nbsp;&nbsp; Total:{" "}
                  {fmt(gi.experience?.total)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderSummary = () => {
    if (!data) return null;
    return (
      <Box sx={{ mt: 4 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#1e3a8a", borderBottom: "2px solid #1e3a8a", pb: 0.5, mb: 1 }}>
          Summary
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 50 }}>Sr. No.</TableCell>
                <TableCell sx={headCellSx}>Criteria</TableCell>
                <TableCell sx={{ ...headCellSx, width: 90 }}>Max. Marks</TableCell>
                <TableCell sx={{ ...headCellSx, width: 110 }}>Self-Assessment Score</TableCell>
                <TableCell sx={{ ...headCellSx, width: 110 }}>Score by Evaluator</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SUMMARY_ROWS.map((r) => (
                <TableRow key={r.key}>
                  <TableCell sx={{ ...cellSx, textAlign: "center", fontWeight: 700 }}>{r.key}</TableCell>
                  <TableCell sx={cellSx}>{r.label}</TableCell>
                  <TableCell sx={{ ...cellSx, textAlign: "center" }}>{r.max}</TableCell>
                  <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                    {totals.sectionSelf[r.key]?.toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                    {evaluating ? totals.sectionEval[r.key]?.toFixed(2) : ""}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={cellSx}></TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800, textAlign: "center" }}>Total</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800, textAlign: "center" }}>250</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800, textAlign: "center" }}>
                  {Object.values(totals.sectionSelf).reduce((a, b) => a + b, 0).toFixed(2)}
                </TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800, textAlign: "center" }}>
                  {evaluating ? totals.finalTotal.toFixed(2) : ""}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={cellSx}></TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800, textAlign: "center" }}>Converted to</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800, textAlign: "center" }}>100</TableCell>
                <TableCell sx={cellSx}></TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800, textAlign: "center" }}>
                  {evaluating ? totals.final100.toFixed(2) : ""}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography sx={{ fontWeight: 700, mt: 2 }}>
          Overall Score: {evaluating ? totals.final100.toFixed(2) : "—"}
        </Typography>
        <Typography sx={{ fontWeight: 700, mt: 0.5 }}>
          Department Rank: {fmt(data.appraisal?.department_rank) || "—"}
        </Typography>

        <Stack direction="row" justifyContent="space-around" sx={{ mt: 5, mb: 2 }}>
          <Typography sx={{ fontWeight: 700 }}>Faculty Member</Typography>
          <Typography sx={{ fontWeight: 700 }}>HoD</Typography>
          <Typography sx={{ fontWeight: 700 }}>Principal</Typography>
        </Stack>
      </Box>
    );
  };

  const renderDocument = () => (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: 2,
        maxWidth: 900,
        mx: "auto",
        backgroundColor: "#ffffff",
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>
          St. Vincent Pallotti College of Engineering &amp; Technology, Nagpur
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Faculty Appraisal</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
          Session: {academicYear}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", textAlign: "right" }}>
          Max. Marks: 250 (Converted to 100)
        </Typography>
      </Box>

      {renderPersonalDetails()}
      {SECTION_LETTERS.map((l) => renderSection(l))}
      {renderSummary()}
    </Paper>
  );

  // Global appraisal toggle: when a super_admin has disabled appraisal, no one
  // can view or apply for it.
  if (appraisalEnabled === false) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          The appraisal feature is currently disabled by the administrator. Please check back later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
        <TextField
          select
          label="Academic Year"
          size="small"
          value={academicYear}
          onChange={(e) => setAcademicYear(e.target.value)}
          sx={{ width: 180, backgroundColor: "#fff" }}
        >
          {ACADEMIC_YEAR_OPTIONS.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
        {data?.appraisal?.status && (
          <Chip
            label={(data.appraisal.status || "draft").toUpperCase()}
            color={
              status === "finalized"
                ? "success"
                : status === "evaluated"
                ? "info"
                : status === "submitted"
                ? "warning"
                : "default"
            }
          />
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {isEvaluator && (
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="My Appraisal" />
          <Tab label="Evaluation Desk" />
        </Tabs>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : tab === 1 && isEvaluator && !data ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Faculty</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Self</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Final /100</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No appraisals found for {academicYear}.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                list.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{fmt(row.full_name || row.email)}</TableCell>
                    <TableCell>{fmt(row.department_name)}</TableCell>
                    <TableCell>
                      <Chip label={(row.status || "draft").toUpperCase()} size="small" />
                    </TableCell>
                    <TableCell>{fmt(row.self_total)}</TableCell>
                    <TableCell>{fmt(row.final_score_100)}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => loadOne(row.id)}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
          {evaluating && (
            <Button size="small" onClick={() => { setData(null); loadList(); }} sx={{ mb: 2 }}>
              ← Back to list
            </Button>
          )}
          {locked && !evaluating && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This appraisal has been {status}. Self-assessment is locked.
            </Alert>
          )}

          {renderDocument()}

          {/* Action bar */}
          <Paper
            elevation={3}
            sx={{
              position: "sticky",
              bottom: 0,
              mt: 2,
              p: 2,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {!evaluating ? (
              <>
                <Button variant="contained" onClick={saveSelf} disabled={saving || locked}>
                  Save Self-Assessment
                </Button>
                <Button
                  variant="outlined"
                  onClick={submitMine}
                  disabled={saving || locked || status !== "draft"}
                >
                  Submit for Evaluation
                </Button>
              </>
            ) : (
              <>
                {!isPureHod && (
                  <Button variant="contained" onClick={saveEvaluator} disabled={saving}>
                    Save Evaluator Scores
                  </Button>
                )}
                {isHodLevel && (
                  <Button variant="outlined" onClick={saveEvaluator} disabled={saving}>
                    Save HoD Portfolio
                  </Button>
                )}
                {isHodLevel && (
                  <Button variant="contained" color="success" onClick={finalize} disabled={saving}>
                    Finalize
                  </Button>
                )}
              </>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default Appraisal;
